import { Injectable } from '@angular/core';

import { ListResponse } from '../../models/list-response';
import { User, UserFilters } from '../../models/user';

import { ApiService } from '../api/api.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class UserService {
  /**
   * A service providing access to users via the API.
   */

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
  ) {}

  /**
   * Get a page of users from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of users per page.
   * @param filters - The set of filters to apply to the users.
   * @return The list of users.
   */
  getUsers(pageNumber?: number,
           pageSize?: number,
           filters?: UserFilters): Promise<ListResponse<User>> {
    return this.apiService.getCollection<User, UserFilters>(
      'users/',
      pageNumber,
      pageSize,
      filters
    );
  }

  /**
   * Get an individual user from the API.
   *
   * @param id - The database ID of the user.
   * @return The user.
   */
  getUser(id: number): Promise<User> {
    return this.apiService.getItem<User>(
      'users/',
      id
    );
  }

  /**
   * Post a new user to the API.
   *
   * @param user - The new User object to create.
   * @return The new user returned from the API.
   */
  addUser(user: User): Promise<User> {
    return this.apiService.postToAPI<User>(
      'users/',
      {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        user_type: user.user_type,
        default_gym_id: user.default_gym_id,
        is_active: user.is_active,
        can_log_in_to_staff_site: user.can_log_in_to_staff_site,
        can_manage_events: user.can_manage_events,
        can_manage_members: user.can_manage_members,
        can_manage_customer_proficiencies: user.can_manage_customer_proficiencies,
        can_export_customers: user.can_export_customers,
        can_access_reports: user.can_access_reports,
        has_griptonite_access: user.has_griptonite_access,
        can_look_up_members_by_barcode: user.can_look_up_members_by_barcode,
        is_organization_admin: user.is_organization_admin,
        pin_mode_required: user.pin_mode_required
      }
    );
  }

  /**
   * Update a user by making a PATCH request to the API.
   *
   * @param user - The user, with updated values.
   * @return The updated user returned from the API.
   */
  updateUser(user: User): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        user_type: user.user_type,
        can_log_in_to_staff_site: user.can_log_in_to_staff_site,
        can_manage_events: user.can_manage_events,
        can_manage_members: user.can_manage_members,
        can_manage_customer_proficiencies: user.can_manage_customer_proficiencies,
        can_export_customers: user.can_export_customers,
        can_access_reports: user.can_access_reports,
        has_griptonite_access: user.has_griptonite_access,
        can_look_up_members_by_barcode: user.can_look_up_members_by_barcode,
        is_organization_admin: user.is_organization_admin,
        pin_mode_required: user.pin_mode_required
      }
    );
  }

  updateUserLightspeedInfo(user: User): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {
        lightspeed_user_id: user.lightspeed_user_id,
        lightspeed_user_username: user.lightspeed_user_username,
        lightspeed_user_display_name: user.lightspeed_user_display_name,
      }
    );
  }

  setUserActiveStatus(user: User): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {
        is_active: user.is_active,
      }
    );
  }

  /**
   * Update a user's password by making a PATCH request to the API.
   *
   * @param user - The user to update.
   * @param newPassword - The user's new password.
   */
  changeUserPassword(user: User, newPassword: string): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {password: newPassword}
    );
  }

  /**
   * Remove a user's password by making a PATCH request to the API.
   *
   * @param user - The user to update.
   */
  removeUserPassword(user: User): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {remove_password: true}
    );
  }

  /**
   * Update a user's pin by making a PATCH request to the API.
   *
   * @param user - The user to update.
   * @param newPin - The user's new pin.
   */
  changeUserPin(user: User, newPin: string): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {pin: newPin}
    );
  }

  /**
   * Remove a user's pin by making a PATCH request to the API.
   *
   * @param user - The user to update.
   */
  removeUserPin(user: User): Promise<User> {
    return this.apiService.patchToAPI<User>(
      user.url,
      {remove_pin: true}
    );
  }

  /**
   * Remove a user's one-time password secret (used for two-factor
   * authentication).
   */
  removeUserOTP(user: User): Promise<void> {
    return this.apiService
      .postToAPIRawURL<any>(
        user.url + 'otp/remove/',
        {}
      )
      .then();
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    return this.http
      .post<any>(
        environment.api_url + 'request-user-password-reset/',
        {email},
        {headers: environment.api_headers}
      )
      .toPromise()
      .then();
  }

  getAPIIntegrationToken(user: User): Promise<string> {
    return this.apiService
      .postToAPI<string>(
         'get-api-integration-token/' + user.id.toString() + '/',
        {}
      );
  }
}
